import { useEffect, useRef, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import {
  getCustomerHistory,
  getCustomerHistoryDetail,
  repeatCustomerHistory,
} from "../../../../api/cart";
import {
  flushAllProductChanges,
  hasAnyPendingProductChanges,
} from "../../../../services/productSyncQueue";
import {
  selectCustomerCart,
  initRequestPayments,
} from "../../../../reducers/userConfigSlice";
import { formatMoney } from "../../../../utils/pricing";

const date = (value) => {
  const d = new Date(value);
  return Number.isFinite(d.getTime())
    ? d.toLocaleString("es-ES")
    : "Fecha no disponible";
};
const money = (value) =>
  value == null ? "Importe a recalcular" : formatMoney(Number(value));
const label = {
  pending: "Pendiente",
  inPreparation: "En preparación",
  prepared: "Preparado",
  delivered: "Entregado",
  inDelivery: "En reparto",
  ACTIVE: "Activo",
  ABANDONED: "Abandonado",
  REPLACED: "Anterior",
  cancelled: "Cancelado",
};
const Notes = ({ notes = [] }) =>
  notes.length ? (
    <div className="customerHistoryNotes">
      <h4>Notas</h4>
      {notes.map((note, i) => (
        <p key={i}>{note}</p>
      ))}
    </div>
  ) : null;
const Lines = ({ lines }) => (
  <ul className="customerHistoryLines">
    {lines.map((line, index) => (
      <li key={index}>
        <div>
          <strong>
            {line.quantity} × {line.name}
          </strong>
          {line.info && <p>{line.info}</p>}
          {line.note && <p className="customerHistoryNote">{line.note}</p>}
        </div>
        <strong>
          {line.lineTotalWithTax == null ? "—" : money(line.lineTotalWithTax)}
        </strong>
      </li>
    ))}
  </ul>
);
export const CustomerHistoryPanel = ({
  customerId,
  kind,
  address,
  onAddress,
  onRepeated,
  onBusy,
}) => {
  const store = useStore(),
    dispatch = useDispatch();
  const [page, setPage] = useState(1),
    [records, setRecords] = useState(null),
    [selected, setSelected] = useState(null),
    [detail, setDetail] = useState(null),
    [quote, setQuote] = useState(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(false),
    [busy, setBusy] = useState(false),
    [retry, setRetry] = useState(0);
  const sending = useRef(false),
    intent = useRef(null),
    alive = useRef(true);
  const shippingMethodId =
    store.getState().aplicationConfig.userConfig.selectedShippingMethodId;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    setRecords(null);
    setError("");
    setLoading(true);
    getCustomerHistory(
      customerId,
      kind,
      page,
      store.getState().aplicationConfig.userConfig.cart?.shoppingCartId,
    )
      .then((response) => {
        if (!active) return;
        if (response?.error !== false || !Array.isArray(response.data?.records))
          throw Error(response?.message || "No se pudo cargar el historial.");
        setRecords(response.data);
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [customerId, kind, page, retry, store]);
  useEffect(() => {
    if (!selected) return;
    let active = true;
    setDetail(null);
    setQuote(null);
    setError("");
    setLoading(true);
    (async () => {
      const response = await getCustomerHistoryDetail(
        customerId,
        kind,
        selected.id,
      );
      if (response?.error !== false || !Array.isArray(response.data?.lines))
        throw Error(response?.message || "No se pudo cargar el desglose.");
      if (!active) return;
      setDetail(response.data);
      if (address?.addressId) {
        const result = await repeatCustomerHistory(
          customerId,
          kind,
          selected.id,
          { addressId: address.addressId, shippingMethodId, preview: true },
        );
        if (result?.error !== false)
          throw Error(result?.message || "No se pudo calcular la nueva cesta.");
        if (active) setQuote(result.data);
      }
    })()
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selected, customerId, kind, address?.addressId, shippingMethodId, retry]);
  const repeat = async () => {
    if (sending.current || !quote || !address?.addressId) return;
    sending.current = true;
    setBusy(true);
    onBusy(true);
    setError("");
    try {
      if (!(await flushAllProductChanges()))
        throw Error("Espera a que se guarden los cambios de la comanda.");
      const before = store.getState().aplicationConfig.userConfig;
      if (before.client?.customerId !== customerId || before.order?.orderId)
        throw Error(
          "La venta ha cambiado. Vuelve a abrir la ficha del cliente.",
        );
      const storageKey = `tpv-repeat:${customerId}:${kind}:${selected.id}:${address.addressId}:${shippingMethodId}`;
      if (!intent.current) {
        let previous;
        try {
          previous = JSON.parse(sessionStorage.getItem(storageKey));
        } catch {
          /* An unreadable cached intent is replaced before sending. */
        }
        intent.current = previous || {
          requestId: window.crypto.randomUUID(),
          reviewToken: quote.reviewToken,
          addressId: address.addressId,
          shippingMethodId,
        };
        sessionStorage.setItem(storageKey, JSON.stringify(intent.current));
      }
      const response = await repeatCustomerHistory(
        customerId,
        kind,
        selected.id,
        intent.current,
      );
      if (response?.error !== false) {
        if (response?.data?.code === "REVIEW_CHANGED") {
          sessionStorage.removeItem(storageKey);
          intent.current = null;
          setQuote(null);
        }
        throw Error(
          response?.message ||
            "No se pudo confirmar la repetición. Reintenta la misma solicitud.",
        );
      }
      if (!response.data?.cart?.shoppingCartId)
        throw Error(
          "La cesta se ha creado, pero falta cargarla. Reintenta para recuperar la misma cesta.",
        );
      if (!alive.current) return;
      const current = store.getState().aplicationConfig.userConfig;
      if (
        current.client?.customerId !== customerId ||
        current.cart !== before.cart ||
        current.order?.orderId ||
        hasAnyPendingProductChanges()
      )
        throw Error(
          "La cesta se ha creado y la venta ha cambiado. Reselecciona el cliente para recuperar su cesta activa.",
        );
      const cart = response.data.cart;
      if (cart.customerId !== customerId || cart.state !== "ACTIVE")
        throw Error("No se pudo verificar la cesta recibida. Revisa la venta.");
      const addresses = current.client.addresses?.shippingAddresses || [];
      dispatch(
        selectCustomerCart({
          client: {
            ...current.client,
            addresses: {
              ...current.client.addresses,
              shippingAddresses: [
                cart.address,
                ...addresses.filter(
                  (a) => a.addressId !== cart.address.addressId,
                ),
              ],
            },
          },
          carts: [cart],
          cart,
        }),
      );
      dispatch(initRequestPayments());
      sessionStorage.removeItem(storageKey);
      intent.current = null;
      onRepeated();
    } catch (e) {
      if (alive.current)
        setError(e.message || "No se pudo repetir. Revisa la conexión.");
    } finally {
      sending.current = false;
      if (alive.current) {
        setBusy(false);
        onBusy(false);
      }
    }
  };
  return (
    <section className="customerHistory" aria-busy={loading || busy}>
      {selected ? (
        <>
          <button
            className="customerHistoryBack"
            disabled={busy}
            onClick={() => {
              setSelected(null);
              setError("");
              setDetail(null);
              setQuote(null);
              intent.current = null;
            }}
          >
            ← Volver al historial
          </button>
          <h3>{selected.label}</h3>
          <p>{date(selected.date)}</p>
          {detail && (
            <>
              <h4>Desglose guardado</h4>
              <Lines lines={detail.lines} />
              <Notes notes={detail.notes} />
              {detail.total != null && (
                <p className="customerHistoryTotal">
                  Total guardado <strong>{money(detail.total)}</strong>
                </p>
              )}
            </>
          )}
          <div className="customerRepeatAddress">
            <div>
              <small>DIRECCIÓN PARA VOLVER A PEDIR</small>
              <strong>{address?.street || "Selecciona una dirección"}</strong>
              {address && (
                <span>
                  {[address.number, address.zipCode, address.city]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              )}
            </div>
            <button disabled={busy} onClick={onAddress}>
              Elegir dirección
            </button>
          </div>
          {quote && (
            <>
              <h4>Nueva cesta · precios actuales</h4>
              <Lines lines={quote.lines} />
              <Notes notes={quote.notes} />
              <p className="customerHistoryTotal">
                Total actual <strong>{money(quote.amounts?.total)}</strong>
              </p>
              <p>
                Se abrirá una cesta nueva. La cesta actual y el registro
                original se conservan. Los descuentos del pedido anterior no se
                copian.
              </p>
            </>
          )}
          {!address?.addressId && (
            <p role="status">
              Selecciona una dirección antes de volver a pedir.
            </p>
          )}
          <button
            className="tpvPrimary customerRepeatButton"
            disabled={busy || loading || !quote || !address?.addressId}
            onClick={repeat}
          >
            {busy
              ? "Creando cesta…"
              : intent.current
                ? "Reintentar la misma solicitud"
                : "Volver a pedir"}
          </button>
        </>
      ) : (
        <>
          <h3>
            {kind === "orders" ? "Pedidos anteriores" : "Carritos anteriores"}
          </h3>
          {records?.records.length === 0 && (
            <p>
              No hay {kind === "orders" ? "pedidos" : "otros carritos"} de este
              cliente.
            </p>
          )}
          <div className="customerHistoryList">
            {records?.records.map((record) => (
              <button
                key={record.id}
                disabled={busy}
                onClick={() => setSelected(record)}
              >
                <div>
                  <strong>{record.label}</strong>
                  <span>{date(record.date)}</span>
                  <small>{label[record.status] || "Guardado"}</small>
                </div>
                <strong>{money(record.total)}</strong>
                <span aria-hidden="true">›</span>
              </button>
            ))}
          </div>
          {records && records.totalPages > 1 && (
            <div className="customerHistoryPagination">
              <button
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
              >
                Anterior
              </button>
              <span>
                {page} de {records.totalPages}
              </span>
              <button
                disabled={page >= records.totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
      {loading && <p role="status">Cargando…</p>}
      {error && (
        <div role="alert">
          <p>{error}</p>
          {!busy && (
            <button onClick={() => setRetry((n) => n + 1)}>
              Reintentar lectura
            </button>
          )}
        </div>
      )}
    </section>
  );
};
